import React, { useCallback, useRef, useState } from 'react';
import {
  ModalDataInfiniteScrollParams,
  useGetStatsSourceStoresQuery,
} from '../../apiCalculationStatistics';
import { getDefaultSorting, InfiniteScrollTable, Text } from '@ydistri/ds';
import { CalculationStatisticStoreSourceResponse } from '@ydistri/api-sdk';
import { TableProps } from 'antd';
import EditRedistributionButton from '../../../Editation/EditRedistributionButton';
import StatisticsModalTableFooter from '../StatisticsModalTableFooter';
import { useTableSourceStoreColumns } from '../columns/useTableSourceStoreColumns';
import { computeRemSize } from '@ydistri/theme';

interface StatisticsModalTableSourceStoresProps {
  calculationId: number;
  params: ModalDataInfiniteScrollParams;
  setParams: React.Dispatch<React.SetStateAction<ModalDataInfiniteScrollParams>>;
  displayDepartment?: boolean;
}

const StatisticsModalTableSourceStores: React.FC<StatisticsModalTableSourceStoresProps> = ({
  calculationId,
  params,
  setParams,
  displayDepartment = false,
}) => {
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]); //selected keys need to be controlled for reset
  const selectedRowsRef = useRef<CalculationStatisticStoreSourceResponse[]>([]);

  const columns = useTableSourceStoreColumns(displayDepartment);

  const { data, isFetching } = useGetStatsSourceStoresQuery({
    ...params,
    sortings: params.sortings ?? getDefaultSorting(columns),
  });

  const rowSelection: TableProps<CalculationStatisticStoreSourceResponse>['rowSelection'] = {
    type: 'checkbox',
    onChange: (
      selectedRowKeys: React.Key[],
      selectedRows: CalculationStatisticStoreSourceResponse[],
    ) => {
      setSelectedRowKeys(selectedRowKeys);
      selectedRowsRef.current = selectedRows;
    },
    selectedRowKeys,
    preserveSelectedRowKeys: false,
  };

  const resetSelection = useCallback(() => {
    setSelectedRowKeys([]);
    selectedRowsRef.current = [];
  }, []);

  return (
    <>
      <InfiniteScrollTable<CalculationStatisticStoreSourceResponse, ModalDataInfiniteScrollParams>
        id="statistics-modal-source-stores-table"
        rowSelection={rowSelection}
        rowKey={r => `${r.sourceStore.id}-${r.department?.id}`}
        columns={columns}
        dataSource={data}
        loading={isFetching}
        size="small"
        setParams={setParams}
        height={computeRemSize(400)}
      />

      <StatisticsModalTableFooter key="footer">
        {selectedRowKeys.length > 0 && (
          <>
            <Text>{selectedRowKeys.length} source stores selected</Text>
            <EditRedistributionButton
              calculationId={calculationId}
              text="Remove..."
              sourceStores={selectedRowsRef.current}
              useDepartments={displayDepartment}
              onClick={resetSelection}
            />
          </>
        )}
      </StatisticsModalTableFooter>
    </>
  );
};

export default StatisticsModalTableSourceStores;
